import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  Chip
} from "@mui/material";
import { Iconify } from "../Minimal/src/components/iconify/iconify";
import { toast } from "sonner";

const NumericKeypadQuickOrder = ({
  open,
  onClose,
  onConfirm,
  initialValue = "",
  title = "Enter Value",
  error = "",
  min = 1,
  max = 9999,
  canSplit = false,
  initialOrderType = "Box",
  onOrderTypeChange = null,
  allowDecimal = false
}) => {
  const [value, setValue] = useState("");
  const [shouldReplace, setShouldReplace] = useState(false);
  const [highlightedKey, setHighlightedKey] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderType, setOrderType] = useState(initialOrderType);
  const dialogContentRef = useRef(null);
  const isOpenRef = useRef(false);
  const initializedRef = useRef(false);
  const openTimeRef = useRef(0);
  const lastKeyPressRef = useRef({ key: null, timestamp: 0, source: null });

  useEffect(() => {
    if (open && !isOpenRef.current) {
      isOpenRef.current = true;
      initializedRef.current = false;
      openTimeRef.current = Date.now();
      lastKeyPressRef.current = { key: null, timestamp: 0, source: null };
      setValue(String(initialValue || ""));
      setShouldReplace(true);
      setIsProcessing(false);
      setHighlightedKey(null);
      setOrderType(initialOrderType || "Box");
    } else if (!open && isOpenRef.current) {
      isOpenRef.current = false;
      initializedRef.current = false;
      openTimeRef.current = 0;
      lastKeyPressRef.current = { key: null, timestamp: 0, source: null };
      setValue("");
      setShouldReplace(false);
      setIsProcessing(false);
      setHighlightedKey(null);
      setOrderType("Box");
    }
  }, [open, initialValue, initialOrderType]);

  const highlightKey = useCallback((key) => {
    setHighlightedKey(key);
    setTimeout(() => setHighlightedKey(null), 200);
  }, []);

  const handleKeyPress = useCallback((key, source = 'unknown') => {
    if (isProcessing || !isOpenRef.current || !initializedRef.current) return;

    const now = Date.now();
    const lastPress = lastKeyPressRef.current;
    
    if (lastPress.key === key && now - lastPress.timestamp < 150) {
      if (lastPress.source !== source || (lastPress.source === source && now - lastPress.timestamp < 50)) {
        return;
      }
    }
    
    lastKeyPressRef.current = { key, timestamp: now, source };
    highlightKey(key);

    if (key === "backspace") {
      setValue((prev) => prev.slice(0, -1));
      setShouldReplace(false);
    } else if (key === "clear") {
      setValue("");
      setShouldReplace(false);
    } else if (key === ".") {
      if (!allowDecimal) {
        // toast.warning("This category won't support this type of quantity");
        return;
      }
      if (shouldReplace) {
        setValue("0.");
        setShouldReplace(false);
      } else if (!value.includes(".")) {
        setValue((prev) => prev + ".");
      }
    } else {
      if (shouldReplace) {
        setValue(key);
        setShouldReplace(false);
      } else {
        setValue((prev) => {
          const newValue = prev + key;
          const decimalIndex = newValue.indexOf(".");
          if (decimalIndex === -1) {
            if (newValue.length <= 4) {
              return newValue;
            }
            return prev;
          } else {
            if (!allowDecimal) return prev;
            const integerPart = newValue.substring(0, decimalIndex);
            const decimalPart = newValue.substring(decimalIndex + 1);
            if (integerPart.length <= 4 && decimalPart.length <= 2) {
              return newValue;
            }
            return prev;
          }
        });
      }
    }
  }, [isProcessing, shouldReplace, value, highlightKey, allowDecimal]);

  const handleConfirm = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isProcessing || !isOpenRef.current) return;

    if (value === "" || value === ".") {
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < min || numValue > max) {
      return;
    }

    setIsProcessing(true);

    if (onConfirm && typeof onConfirm === 'function') {
      try {
        onConfirm(value, orderType);
      } catch (error) {
        console.error("Error in onConfirm:", error);
      } finally {
        setIsProcessing(false);
      }
    } else {
      setIsProcessing(false);
    }
  }, [value, min, max, onConfirm, isProcessing, orderType]);

  const handleClose = useCallback((event, reason) => {
    if (isProcessing) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (!isOpenRef.current) {
      return;
    }

    const timeSinceOpen = Date.now() - openTimeRef.current;
    if (timeSinceOpen < 500 && reason !== 'escapeKeyDown') {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (reason === 'backdropClick') {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (reason === 'escapeKeyDown') {
      if (onClose && typeof onClose === 'function') {
        onClose();
      }
      return;
    }

    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  }, [onClose, isProcessing]);

  const handleOrderTypeToggle = useCallback((newOrderType) => {
    setOrderType(newOrderType);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (isProcessing || !isOpenRef.current || !initializedRef.current) {
      e.preventDefault();
      return;
    }

    const target = e.target;
    if (target && (target.tagName === 'BUTTON' || target.closest('button'))) {
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleConfirm(e);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
    } else if (e.key === "Backspace") {
      e.preventDefault();
      handleKeyPress("backspace", 'keyboard');
    } else if (canSplit && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
      e.preventDefault();
      e.stopPropagation();
      const newOrderType = orderType === "Box" ? "Piece" : "Box";
      handleOrderTypeToggle(newOrderType);
    } else if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      handleKeyPress(e.key, 'keyboard');
    } else if (e.key === ".") {
      e.preventDefault();
      handleKeyPress(".", 'keyboard');
    }
  }, [isProcessing, handleKeyPress, handleConfirm, handleClose, canSplit, orderType, handleOrderTypeToggle]);

  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    [".", "0", "backspace"],
    ["clear", "", ""]
  ];

  const displayValue = value || "0";
  const numValue = parseFloat(value);
  const isValid = value !== "" && value !== "." && !isNaN(numValue) && numValue >= min && numValue <= max;

  const handleDialogEntered = useCallback(() => {
    if (dialogContentRef.current && isOpenRef.current) {
      dialogContentRef.current.focus();
    }
    initializedRef.current = true;
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth={false}
      onKeyDown={handleKeyDown}
      onEntered={handleDialogEntered}
      disableEnforceFocus={false}
      disableEscapeKeyDown={isProcessing}
      PaperProps={{
        sx: {
          borderRadius: 0,
          boxShadow: 4,
          zIndex: 1500,
          maxWidth: '320px',
          width: '100%'
        }
      }}
      sx={{
        zIndex: 1500
      }}
      slotProps={{
        backdrop: {
          sx: {
            zIndex: 1499
          }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.5, px: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
            {title}
          </Typography>
          <IconButton 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isProcessing) {
                handleClose();
              }
            }} 
            size="small"
            disabled={isProcessing}
            sx={{ p: 0.5 }}
          >
            <Iconify icon="mdi:close" width={18} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        ref={dialogContentRef}
        tabIndex={-1}
        sx={{ outline: 'none', px: 2, py: 1.5 }}
      >
        {canSplit && (
          <Box sx={{ display: "flex", width: '100%', mb: 1.5 }}>
            <Chip
              onClick={() => handleOrderTypeToggle('Box')}
              sx={{
                cursor: 'pointer',
                flex: 1,
                '&.MuiChip-root': {
                  border: '0.5px solid',
                  borderColor: orderType === 'Box' ? 'error.main' : '#E0E0E0',
                  borderRadius: 0,
                  width: '100%',
                  color: orderType === 'Box' ? 'error.main' : 'text.primary',
                  ...(orderType === 'Box' ? {} : { backgroundColor: 'white' }),
                  fontSize: '12px',
                  height: '30px'
                }
              }}
              color={orderType === 'Box' ? 'error' : 'default'}
              label="Box"
              variant="soft"
              size="small"
            />
            <Chip
              onClick={() => handleOrderTypeToggle('Piece')}
              sx={{
                cursor: 'pointer',
                flex: 1,
                '&.MuiChip-root': {
                  border: '0.5px solid',
                  borderColor: orderType === 'Piece' ? 'error.main' : '#E0E0E0',
                  borderRadius: 0,
                  width: '100%',
                  color: orderType === 'Piece' ? 'error.main' : 'text.primary',
                  ...(orderType === 'Piece' ? {} : { backgroundColor: 'white' }),
                  fontSize: '12px',
                  height: '30px'
                }
              }}
              color={orderType === 'Piece' ? 'error' : 'default'}
              label="Piece"
              variant="soft"
              size="small"
            />
          </Box>
        )}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 1.5,
            backgroundColor: "grey.100",
            textAlign: "right",
            minHeight: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end"
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              color: isValid ? "text.primary" : "error.main",
              fontFamily: "monospace",
              fontSize: '1.5rem'
            }}
          >
            {displayValue}
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "grid", gap: 0.75 }}>
          {keys.map((row, rowIndex) => (
            <Box 
              key={rowIndex} 
              sx={{ 
                display: "grid", 
                gridTemplateColumns: row.filter(k => k !== "").length === 1 ? "1fr" : "1fr 1fr 1fr", 
                gap: 0.75 
              }}
            >
              {row.map((key, keyIndex) => {
                if (key === "") {
                  return null;
                }

                let icon = null;
                let label = key;
                let color = "primary";

                if (key === "backspace") {
                  icon = <Iconify icon="ic:round-backspace" width={20} />;
                  label = "";
                  color = "error";
                } else if (key === "clear") {
                  label = "C";
                  color = "warning";
                } else if (key === ".") {
                  label = ".";
                  color = "primary";
                }

                const isHighlighted = highlightedKey === key;

                return (
                  <Button
                    key={`${rowIndex}-${keyIndex}-${key}`}
                    variant="contained"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleKeyPress(key, 'button');
                    }}
                    disabled={isProcessing}
                    sx={{
                      height: 48,
                      fontSize: "1rem",
                      fontWeight: 600,
                      minWidth: 0,
                      backgroundColor: isHighlighted 
                        ? (color === "primary" ? "#4CAF50" : (color === "error" ? "#f44336" : "#ff9800"))
                        : (color === "primary" ? "grey.800" : `${color}.main`),
                      color: "white",
                      boxShadow: isHighlighted ? 4 : 1,
                      transform: isHighlighted ? "scale(0.95)" : "scale(1)",
                      transition: "all 0.15s ease-in-out",
                      border: isHighlighted ? "2px solid #fff" : "none",
                      "&:hover": {
                        backgroundColor: isHighlighted
                          ? (color === "primary" ? "#4CAF50" : (color === "error" ? "#f44336" : "#ff9800"))
                          : (color === "primary" ? "primary.main" : `${color}.dark`),
                        color: "white",
                        transform: isHighlighted ? "scale(0.95)" : "scale(1.02)",
                        boxShadow: isHighlighted ? 4 : 2
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                        boxShadow: 1
                      },
                      "&:disabled": {
                        opacity: 0.6
                      }
                    }}
                  >
                    {icon || label}
                  </Button>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5, textAlign: "center", fontSize: '0.7rem' }}
        >
          Valid range: {min} - {max}
        </Typography> */}

        {canSplit && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Box component="kbd" sx={{
                px: 1,
                py: 0.5,
                fontSize: '0.7rem',
                color: '#637381',
                fontWeight: 600,
                backgroundColor: '#fff',
                border: '1px solid #D0D7DE',
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                fontFamily: 'monospace'
              }}>
                ←
              </Box>
              <Box component="kbd" sx={{
                px: 1,
                py: 0.5,
                fontSize: '0.7rem',
                color: '#637381',
                fontWeight: 600,
                backgroundColor: '#fff',
                border: '1px solid #D0D7DE',
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                fontFamily: 'monospace'
              }}>
                →
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Toggle Box/Piece
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1.5, pt: 1, gap: 0.75 }}>
        <Button 
          variant="outlined" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isProcessing) {
              handleClose();
            }
          }} 
          fullWidth 
          size="medium"
          disabled={isProcessing}
          sx={{ fontSize: '0.875rem', py: 0.75 }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleConfirm}
          disabled={!value || !isValid || isProcessing}
          fullWidth
          size="medium"
          color="primary"
          sx={{ fontSize: '0.875rem', py: 0.75 }}
        >
          {isProcessing ? "Processing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NumericKeypadQuickOrder;

